import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import enUS from 'antd/locale/en_US';
import zhCN from 'antd/locale/zh_CN';
import * as Blockly from 'blockly';
import * as BlocklyEn from 'blockly/msg/en';
import * as BlocklyZhHans from 'blockly/msg/zh-hans';
import { storage } from '@/services/storage';

export type UiLanguageSetting = 'auto' | 'zh-CN' | 'en-US';
export type ResolvedUiLanguage = 'zh-CN' | 'en-US';

const UI_LANGUAGE_EVENT = 'yiblock-ui-language-change';
const DEFAULT_UI_LANGUAGE: UiLanguageSetting = 'auto';

const EXACT_TRANSLATIONS: Record<string, string> = {
  '应用市场': 'Marketplace',
  '管理后台': 'Admin Console',
  '文件': 'File',
  '设置': 'Settings',
  '分享': 'Share',
  '帮助': 'Help',
  '登录': 'Sign In',
  '退出登录': 'Sign Out',
  '新建': 'New',
  '打开': 'Open',
  '保存': 'Save',
  '另存为': 'Save As',
  '导入': 'Import',
  '导出': 'Export',
  '运行': 'Run',
  '停止': 'Stop',
  '烧录/部署': 'Flash / Deploy',
  '导入代码': 'Import Code',
  '工作区设置': 'Workspace Settings',
  '系统设置': 'System Settings',
  '显示网格线': 'Show Grid',
  '对齐到网格线': 'Snap to Grid',
  '主题': 'Theme',
  '浅色': 'Light',
  '深色': 'Dark',
  '界面语言': 'UI Language',
  '编程语言': 'Programming Language',
  '更新于': 'Updated',
  '标准': 'Standard',
  '逻辑': 'Logic',
  '循环': 'Loops',
  '数学': 'Math',
  '文本': 'Text',
  '函数': 'Functions',
  '列表': 'Lists',
  '颜色': 'Color',
  '电脑': 'Computer',
  '字符串': 'Strings',
  '标准IO': 'Standard IO',
  'C语言': 'C Language',
  '舞台': 'Stage',
  '舞台设置': 'Stage Settings',
  '控制': 'Control',
  '运动': 'Motion',
  '外观': 'Looks',
  '声音': 'Sound',
  '事件': 'Events',
  '侦测': 'Sensing',
  '跟随系统': 'Follow System',
  '中文': 'Chinese',
  '英语': 'English',
  'English': 'English',
  '我的应用': 'My Apps',
  '编程区域': 'Programming Area',
  '我的作品': 'My Projects',
  '新建作品': 'New Project',
  '导入作品': 'Import Project',
  '创建第一个作品': 'Create Your First Project',
  '还没有作品': 'No projects yet',
  '编辑作品': 'Edit Project',
  '作品名称': 'Project Name',
  '作品描述': 'Project Description',
  '输入作品名称': 'Enter project name',
  '输入作品描述（可选）': 'Enter project description (optional)',
  '选择模板': 'Select Template',
  '请选择本地作品': 'Please select a local project',
  '上传我的作品': 'Upload My Project',
  '上传': 'Upload',
  '上传本地作品到应用市场': 'Upload Local Project to Marketplace',
  '选择本地作品': 'Select Local Project',
  '发布标题': 'Publish Title',
  '发布描述': 'Publish Description',
  '可选，默认使用本地作品名称': 'Optional, defaults to local project name',
  '可选': 'Optional',
  '搜索': 'Search',
  '下载并打开': 'Download & Open',
  '收藏': 'Favorite',
  '已收藏': 'Favorited',
  '我的评分': 'My Rating',
  '暂无评分': 'No ratings yet',
  '人评分': 'ratings',
  '暂无可用作品': 'No projects available',
  '请先登录后访问应用市场': 'Please sign in to access the marketplace',
  '请先登录后访问管理后台': 'Please sign in to access admin console',
  '只有管理员可访问管理后台': 'Only admins can access the admin console',
  '关于': 'About',
  '版本': 'Version',
  '用户名': 'Username',
  '密码': 'Password',
  '记住密码': 'Remember me',
  '忘记密码？': 'Forgot password?',
  '请输入用户名': 'Please enter username',
  '请输入密码': 'Please enter password',
  '保存并继续': 'Save and Continue',
  '不保存': 'Do Not Save',
  '保存并刷新': 'Save and Refresh',
  '不保存，继续刷新': 'Refresh Without Saving',
  '可能会丢失数据': 'Unsaved Changes',
  '有未保存的内容': 'Unsaved Changes',
  '当前内容尚未保存，是否先保存？': 'Current changes are not saved. Save first?',
  '当前内容尚未保存，刷新后可能丢失，是否先保存？': 'Current changes are not saved. Refresh may lose data. Save first?',
  '发布到市场': 'Publish to Marketplace',
  '共享给好友': 'Share with Friends',
  '分享给微信': 'Share to WeChat',
  '分享给微博': 'Share to Weibo',
  '远程协助': 'Remote Assistance',
  '示例': 'Examples',
  '易联魔方演示': 'YiLink Demo',
  '正在加载示例...': 'Loading examples...',
  '示例加载失败': 'Failed to load examples',
  '创建': 'Create',
  '取消': 'Cancel',
  '确认': 'Confirm',
  '删除': 'Delete',
  '编辑': 'Edit',
  '保存到服务器': 'Save to Server',
  '另存为（服务器副本）': 'Save As (Server Copy)',
  '打开项目文件': 'Open Project File',
  '导入项目': 'Import Project',
  '导出项目': 'Export Project',
  '从代码导入积木': 'Import Blocks from Code',
  '运行程序': 'Run Program',
  '停止运行': 'Stop Running',
  '请先登录后再使用该功能': 'Please sign in first',
  '请先登录后再打开作品': 'Please sign in before opening projects',
  '请先登录后再导入作品': 'Please sign in before importing projects',
  '本地项目不存在': 'Local project does not exist',
  '下载作品失败': 'Failed to download project',
  '收藏失败': 'Failed to favorite',
  '评分失败': 'Failed to submit rating',
  '加载应用市场失败': 'Failed to load marketplace',
  '评分已提交': 'Rating submitted',
  '作品已上传到应用市场': 'Project uploaded to marketplace',
  '已下载到本地作品库': 'Downloaded to local project library',
  '项目创建成功': 'Project created',
  '项目已删除': 'Project deleted',
  '项目已更新': 'Project updated',
  '项目导入成功': 'Project imported',
  '导入失败': 'Import failed',
  '登录成功': 'Sign in successful',
  '登录失败': 'Sign in failed',
  '已退出登录': 'Signed out',
  '登录已失效，请重新登录': 'Session expired, please sign in again',
  '本机运行': 'Run on PC',
  '未选择控制器': 'No controller selected',
  '选择设备': 'Choose Device',
  '更换设备': 'Choose Device',
  '保存到服务器 (Ctrl+S)': 'Save to Server (Ctrl+S)',
  '撤销 (Ctrl+Z)': 'Undo (Ctrl+Z)',
  '重做 (Ctrl+Shift+Z)': 'Redo (Ctrl+Shift+Z)',
  'PC本机运行不支持烧录，请选择支持烧录的控制器，如YC-XXX': 'Local PC mode does not support flashing. Please select a supported controller such as YC-XXX.',
  '外设': 'Peripheral',
  '无线外设': 'Wireless Peripheral',
  '有线外设': 'Wired Peripheral',
  '暂无无线外设': 'No Wireless Peripheral',
  '暂无有线外设': 'No Wired Peripheral',
  '选择无线外设': 'Choose Wireless Peripheral',
  '选择有线外设': 'Choose Wired Peripheral',
  '请先在左侧外设中添加无线外设': 'Add a wireless peripheral from the left sidebar first',
  '请先在左侧外设中添加有线外设': 'Add a wired peripheral from the left sidebar first',
  '用户管理': 'User Management',
  '管理系统': 'Admin System',
  '系统配置': 'System Configuration',
  'AI 模型配置': 'AI Model Configuration',
  'AI 助手统计': 'AI Assistant Statistics',
  '应用管理': 'Application Management',
  '加载后台数据失败': 'Failed to load admin data',
  '用户已创建': 'User created',
  '用户列表与创建': 'Users and Creation',
  '管理员可创建和查看用户；角色分为管理员、指导老师、普通用户。': 'Admins can create and view users; roles are Admin, Teacher, and User.',
  '用户总数': 'Total Users',
  '作品总数': 'Total Projects',
  '公开作品': 'Public Projects',
  '评分记录': 'Rating Records',
  '收藏记录': 'Favorite Records',
  '下载记录': 'Download Records',
  '已修改': 'Modified',
  '打开本地修改版本': 'Open Modified Local Version',
  '创建新副本': 'Create New Copy',
  '角色': 'Role',
  '显示名': 'Display Name',
  '权限': 'Permissions',
  '管理员': 'Admin',
  '指导老师': 'Teacher',
  '普通用户': 'User',
  '复制': 'Copy',
  '添加注释': 'Add Comment',
  '移除注释': 'Remove Comment',
  '删除块': 'Delete Block',
  '折叠块': 'Collapse Block',
  '展开块': 'Expand Block',
  '禁用块': 'Disable Block',
  '启用块': 'Enable Block',
  '在线帮助': 'Online Help',
  'AI 助手': 'AI Assistant',
  'AI 和语音设置': 'AI and Speech Settings',
  'AI 与语音设置': 'AI and Speech Settings',
  '教学模式': 'Teaching Mode',
  '快捷问题：': 'Quick Questions:',
  '文字输入': 'Text Input',
  '语音输入': 'Voice Input',
  '开始语音输入': 'Start Voice Input',
  '停止语音输入': 'Stop Voice Input',
  '开始录音': 'Start Recording',
  '停止录音': 'Stop Recording',
  '转写中...': 'Transcribing...',
  '停止播报': 'Stop Speaking',
  '朗读这条回复': 'Read This Reply',
  '重新执行这条指令': 'Run This Instruction Again',
  '重试': 'Retry',
  '正在调用模型...': 'Calling model...',
  'XML解析': 'XML Parser',
  '载入最近AI XML': 'Load Latest AI XML',
  '导入 XML': 'Import XML',
  '请输入要解析的 XML 或 JSON actions': 'Enter XML or JSON actions to parse',
  '未识别到可导入的 XML 内容': 'No importable XML content was detected',
  'XML 已替换到工作区': 'XML replaced the current workspace',
  'XML 已添加到工作区': 'XML was added to the workspace',
  '粘贴 AI 返回的 XML 或包含 actions 的 JSON，选择追加或替换后导入到工作区。': 'Paste AI XML or JSON actions, then choose append or replace to import into the workspace.',
  '粘贴 <xml>...</xml> 或模型返回的 JSON actions': 'Paste <xml>...</xml> or the model-returned JSON actions',
  '追加': 'Append',
  '替换': 'Replace',
  '模型提供方': 'Model Provider',
  '模型名称': 'Model Name',
  '温度（0~1）': 'Temperature (0-1)',
  '查看模型平台文档': 'View model provider docs',
  '可直接输入自定义模型名': 'You can type a custom model name',
  '语音识别（输入）': 'Speech Recognition (Input)',
  '默认输入方式': 'Default Input Mode',
  '识别服务': 'Recognition Service',
  '识别语言': 'Recognition Language',
  '浏览器内置语音识别': 'Built-in Browser Speech Recognition',
  '当前使用浏览器内置识别能力。': 'Using the browser built-in recognition service.',
  '识别模型、接口地址和 Key 由管理后台统一配置。': 'Recognition model, endpoint, and key are managed in the admin console.',
  '管理后台尚未完成语音识别配置。': 'Speech recognition is not configured in the admin console yet.',
  '查看语音识别文档': 'View speech recognition docs',
  '语音合成（播报）': 'Speech Synthesis (Playback)',
  '自动播报 AI 回复': 'Automatically speak AI replies',
  '播报服务': 'Speech Output Service',
  '浏览器内置语音播报': 'Built-in Browser Speech Synthesis',
  '播报语言': 'Speech Language',
  '语速': 'Rate',
  '音调': 'Pitch',
  '浏览器音色': 'Browser Voice',
  '浏览器可用音色名称': 'Browser voice name',
  '合成模型、音色、接口地址和 Key 由管理后台统一配置。': 'Synthesis model, voice, endpoint, and key are managed in the admin console.',
  '管理后台尚未完成语音播报配置。': 'Speech synthesis is not configured in the admin console yet.',
  '查看语音播报文档': 'View speech synthesis docs',
  '当前浏览器不支持内置语音识别，请联系管理员切换语音识别服务': 'This browser does not support built-in speech recognition. Ask an admin to switch the recognition service.',
  '管理员尚未完成语音识别配置': 'Speech recognition is not configured in admin yet',
  '当前浏览器不支持语音播报': 'This browser does not support speech synthesis',
  '管理员尚未完成语音播报配置': 'Speech synthesis is not configured in admin yet',
  '语音识别结果为空': 'Speech recognition returned empty text',
  '语音识别完成': 'Speech recognition completed',
  '语音识别失败': 'Speech recognition failed',
  '语音播报失败': 'Speech synthesis failed',
  '语音录音失败': 'Voice recording failed',
  '模型名称不能为空': 'Model name cannot be empty',
  '检测到未添加的 YM 外设': 'Missing YM Peripherals Detected',
  'AI 生成的积木依赖以下 YM 外设，请确认添加：': 'The AI-generated blocks depend on the following YM peripherals. Please confirm adding them:',
  '确认后会自动添加到当前项目外设列表，再继续生成积木。': 'After confirmation, these peripherals will be added to the current project before continuing block generation.',
  '确认添加并继续': 'Add and Continue',
  '浏览器环境不可用，无法导入积木。': 'Browser environment unavailable. Cannot import blocks.',
  '工作区导入超时，请重试。': 'Workspace import timed out. Please try again.',
  '缺少 insertBlocks.xml。': 'Missing insertBlocks.xml.',
  '缺少 applyPatch.xml。': 'Missing applyPatch.xml.',
  '追加积木失败。': 'Failed to append blocks.',
  '替换工作区失败。': 'Failed to replace the workspace.',
  '当前浏览器不支持录音能力': 'This browser does not support audio recording',
  '当前浏览器不支持 MediaRecorder 录音': 'This browser does not support MediaRecorder',
  '录音启动失败': 'Failed to start recording',
  '提示：': 'Tip:',
  '我可以继续帮你自动修正相关积木，直接回复："请修正这个问题"。': 'I can continue fixing the related blocks automatically. Reply with: "Please fix this issue."',
  'YM 无线外设模块': 'YM wireless peripheral module',
  '无线音频，可录音和播放。': 'Wireless audio module for recording and playback.',
  '无线蜂鸣器，可设置频率和开关。': 'Wireless buzzer with frequency and on/off control.',
  '环境光传感器，读取光照强度。': 'Ambient light sensor for reading light intensity.',
  '卫星定位模块，读取经纬度/速度等。': 'GNSS module for reading latitude, longitude, speed, and more.',
  '三按键模块，支持按键状态与事件。': 'Three-button module with button states and events.',
  '无线 LED，可设置亮度、模式与闪光。': 'Wireless LED with brightness, mode, and flash control.',
  '人体红外传感器，支持接近/离开事件。': 'PIR sensor with approach and leave events.',
  '红黄绿三色灯状态控制。': 'Red-yellow-green light control.',
  'USB 电源模块，可控制开关与功率。': 'USB power module with switch and power control.',
  '温湿度传感器，可读取温度与湿度。': 'Temperature and humidity sensor.',
  '超声测距模块，读取距离(mm)。': 'Ultrasonic distance sensor reading distance (mm).',
  '5G 通讯模块，支持 MQTT。': '5G communication module with MQTT support.',
  '运行日志': 'Runtime Logs',
  '输出': 'Output',
  '设备日志': 'Device Logs',
  '调试': 'Debug',
  '变量': 'Variables',
  '监视': 'Watch',
  '值': 'Value',
  '暂无变量': 'No variables',
  '暂无数据': 'No data',
  '暂无日志': 'No logs',
  '搜索日志...': 'Search logs...',
  '输入内容...': 'Enter text...',
  '时间戳': 'Timestamp',
  '全部': 'All',
  '错误': 'Error',
  '警告': 'Warning',
  '信息': 'Info',
  '清空': 'Clear',
  '清空日志': 'Clear Logs',
  '发送到 AI 分析': 'Send to AI Analysis',
  '日志已复制': 'Logs copied',
  '复制失败': 'Copy failed',
  '折叠': 'Collapse',
  '全屏': 'Fullscreen',
  '退出全屏': 'Exit Fullscreen',
  '关闭': 'Close',
  '连接设备': 'Connect Device',
  '串口': 'Serial',
  '蓝牙': 'Bluetooth',
  '扫描中...': 'Scanning...',
  '扫描串口设备': 'Scan Serial Devices',
  '正在扫描串口设备...': 'Scanning serial devices...',
  '未发现设备（尚未接入真实串口扫描）': 'No device found (real serial scanning is not integrated yet)',
  '设备名称前缀': 'Device name prefix',
  '扫描蓝牙设备': 'Scan Bluetooth Devices',
  '正在扫描蓝牙设备...': 'Scanning Bluetooth devices...',
  '未发现蓝牙设备': 'No Bluetooth devices found',
  '连接': 'Connect',
  '断开': 'Disconnect',
  '已连接': 'Connected',
  '设备ID：': 'Device ID:',
  '固件版本：': 'Firmware:',
  '硬件版本：': 'Hardware:',
  '型号：': 'Model:',
  '分辨率：': 'Resolution:',
  '网络：': 'Network:',
  '接口：': 'Interface:',
  '未连接设备': 'No device connected',
  '3D 场景': '3D Scene',
  '创建立方体': 'Create Cube',
  '创建球体': 'Create Sphere',
  '碰撞检测占位': 'Collision Placeholder',
  '3D 渲染容器（占位）': '3D Render Container (Placeholder)',
  '对象列表': 'Object List',
  '暂无对象': 'No objects',
  '选择角色': 'Select Role',
  '添加道具': 'Add Prop',
  '设置背景': 'Set Backdrop',
  '上传背景': 'Upload Backdrop',
  '图片背景': 'Image Backdrop',
  '纯色背景': 'Solid Color Backdrop',
  '背景颜色': 'Backdrop Color',
  '置于顶层': 'Bring to Front',
  '上移一层': 'Move Forward',
  '下移一层': 'Move Backward',
  '置于底层': 'Send to Back',
  '已设置背景颜色': 'Backdrop color updated',
  '运行结束': 'Run finished',
  '无线外设连接超时': 'Wireless peripheral connection timed out',
  '连接失败': 'Connection failed',
  '未知型号': 'Unknown Model',
  '未知版本': 'Unknown Version',
  '无线外设断开超时': 'Wireless peripheral disconnect timed out',
  '断开失败': 'Disconnect failed',
  '无线外设写入超时': 'Wireless peripheral write timed out',
  '写入失败': 'Write failed',
  '无线外设读取超时': 'Wireless peripheral read timed out',
  '读取失败': 'Read failed',
  '搜索作品标题/描述': 'Search project title/description',
  '暂无描述': 'No description',
  '发送': 'Send',
  '全局 AI 模型配置': 'Global AI Model Settings',
  'AI 语音配置': 'AI Speech Settings',
  '系统统一管理语音识别与播报模型；AI 助手面板仅保留个人使用偏好。': 'Speech recognition and synthesis models are managed centrally; the AI panel only keeps personal preferences.',
  '语音识别配置': 'Speech Recognition Settings',
  '语音合成配置': 'Speech Synthesis Settings',
  '浏览器内置': 'Built-in Browser',
  '自定义接口': 'Custom Endpoint',
  '默认模型': 'Default Model',
  '默认音色': 'Default Voice',
  '浏览器内置模式不需要配置 Key；自定义接口模式会由后端代转发请求。': 'Built-in browser mode does not require a key; custom endpoint mode is proxied by the backend.',
  '保存语音配置': 'Save Speech Settings',
  'AI 语音配置已保存': 'AI speech settings saved',
  '悟空（小）': 'Wukong (Small)',
  '悟空（3D 左）': 'Wukong (3D Left)',
  '悟空（3D 右）': 'Wukong (3D Right)',
  '花果山': 'Flower Fruit Mountain',
  '工厂': 'Factory',
  '水帘洞': 'Water Curtain Cave',
  '筋斗云': 'Somersault Cloud',
};

const PATTERN_TRANSLATIONS: Array<{ pattern: RegExp; replace: (...args: string[]) => string }> = [
  {
    pattern: /^更新于\s*(.+)$/,
    replace: (_, date) => `Updated ${date}`,
  },
  {
    pattern: /^版本\s*(.+)$/,
    replace: (_, version) => `Version ${version}`,
  },
  {
    pattern: /^已创建对象变量：(.+)$/,
    replace: (_, name) => `Object variable created: ${name}`,
  },
  {
    pattern: /^AI\s*积木导入失败[:：]\s*(.+)$/,
    replace: (_, reason) => `AI block import failed: ${reason}`,
  },
  {
    pattern: /^已连接：(.+)$/,
    replace: (_, name) => `Connected: ${name}`,
  },
  {
    pattern: /^已选择角色：(.+)$/,
    replace: (_, name) => `Role selected: ${name}`,
  },
  {
    pattern: /^已添加道具：(.+)$/,
    replace: (_, name) => `Prop added: ${name}`,
  },
  {
    pattern: /^已选择背景：(.+)$/,
    replace: (_, name) => `Backdrop selected: ${name}`,
  },
  {
    pattern: /^舞台运行失败[:：]\s*(.+)$/,
    replace: (_, reason) => `Stage run failed: ${reason}`,
  },
  {
    pattern: /^订阅日志失败[:：]\s*(.+)$/,
    replace: (_, reason) => `Log subscription failed: ${reason}`,
  },
  {
    pattern: /^语音识别失败[:：]\s*(.+)$/,
    replace: (_, reason) => `Speech recognition failed: ${reason}`,
  },
  {
    pattern: /^语音播报失败[:：]\s*(.+)$/,
    replace: (_, reason) => `Speech synthesis failed: ${reason}`,
  },
  {
    pattern: /^语音录音失败[:：]\s*(.+)$/,
    replace: (_, reason) => `Voice recording failed: ${reason}`,
  },
  {
    pattern: /^AI 请求失败[:：]\s*(.+)$/,
    replace: (_, reason) => `AI request failed: ${reason}`,
  },
  {
    pattern: /^AI 提示[:：]\s*(.+)$/,
    replace: (_, reason) => `AI notice: ${reason}`,
  },
  {
    pattern: /^正在扫描\s+(.+)\s+设备，请在浏览器弹窗中选择要连接的设备\.\.\.$/,
    replace: (_, name) => `Scanning ${name} devices. Choose a device in the browser dialog...`,
  },
  {
    pattern: /^已连接\s+(.+)（型号[:：]\s*(.+)，固件[:：]\s*(.+)）$/,
    replace: (_, name, model, firmware) => `Connected ${name} (Model: ${model}, Firmware: ${firmware})`,
  },
  {
    pattern: /^(.+)\s+尚未建立连接句柄，已跳过本次写入$/,
    replace: (_, peripheral) => `${peripheral} has no connection handle yet. This write was skipped.`,
  },
  {
    pattern: /^(.+)\s+尚未建立连接句柄，已跳过本次读取$/,
    replace: (_, peripheral) => `${peripheral} has no connection handle yet. This read was skipped.`,
  },
  {
    pattern: /^已自动添加 YM 外设[:：]\s*(.+)$/,
    replace: (_, items) => `YM peripherals added automatically: ${items}`,
  },
  {
    pattern: /^已取消本次积木导入（未添加 YM 外设[:：]\s*(.+)）$/,
    replace: (_, items) => `Block import canceled because YM peripherals were not added: ${items}`,
  },
  {
    pattern: /^已取消本次生成：缺少外设\s+(.+)，且未确认添加。$/,
    replace: (_, items) => `Generation canceled because peripherals ${items} were not confirmed.`,
  },
  {
    pattern: /^AI 已应用\s*(\d+)\s*处积木变更到当前工作区$/,
    replace: (_, count) => `AI applied ${count} block changes to the current workspace`,
  },
  {
    pattern: /^积木导入失败[:：]\s*(.+)$/,
    replace: (_, reason) => `Block import failed: ${reason}`,
  },
  {
    pattern: /^位置[:：]\s*\((.+)\)$/,
    replace: (_, coords) => `Position: (${coords})`,
  },
  {
    pattern: /^第\s*(\d+)\s*行超过\s*(\d+)\s*字节，无法按行分块发送$/,
    replace: (_, line, bytes) => `Line ${line} exceeds ${bytes} bytes and cannot be chunked by line`,
  },
  {
    pattern: /^管理后台自定义识别：(.+)$/,
    replace: (_, model) => `Admin-configured recognition: ${model}`,
  },
  {
    pattern: /^管理后台自定义播报：(.+)\s*\/\s*(.+)$/,
    replace: (_, model, voice) => `Admin-configured speech: ${model} / ${voice}`,
  },
  {
    pattern: /^已收到日志摘要：\n([\s\S]+)\n\n我可以继续帮你自动修正相关积木，直接回复："请修正这个问题"。$/,
    replace: (_, summary) => `Log summary received:\n${summary}\n\nI can continue fixing the related blocks automatically. Reply with: "Please fix this issue."`,
  },
  {
    pattern: /^立方体\s*(\d+)$/,
    replace: (_, index) => `Cube ${index}`,
  },
  {
    pattern: /^球体\s*(\d+)$/,
    replace: (_, index) => `Sphere ${index}`,
  },
  {
    pattern: /^(\d+)\s*星（([\d.]+)\s*分，(\d+)\s*人评分）$/,
    replace: (_, stars, score, count) => `${stars} stars (${score}, ${count} ratings)`,
  },
  {
    pattern: /^下载[:：]\s*(\d+)$/,
    replace: (_, count) => `Downloads: ${count}`,
  },
  {
    pattern: /^更新[:：]\s*(.+)$/,
    replace: (_, value) => `Updated: ${value}`,
  },
  {
    pattern: /^(.+)-市场导入$/,
    replace: (_, title) => `${title} - Imported from Marketplace`,
  },
  {
    pattern: /^设备ID[:：]\s*(.+)$/,
    replace: (_, value) => `Device ID: ${value}`,
  },
  {
    pattern: /^固件版本[:：]\s*(.+)$/,
    replace: (_, value) => `Firmware: ${value}`,
  },
  {
    pattern: /^硬件版本[:：]\s*(.+)$/,
    replace: (_, value) => `Hardware: ${value}`,
  },
  {
    pattern: /^型号[:：]\s*(.+)$/,
    replace: (_, value) => `Model: ${value}`,
  },
  {
    pattern: /^分辨率[:：]\s*(.+)$/,
    replace: (_, value) => `Resolution: ${value}`,
  },
  {
    pattern: /^网络[:：]\s*(.+)$/,
    replace: (_, value) => `Network: ${value}`,
  },
  {
    pattern: /^接口[:：]\s*(.+)$/,
    replace: (_, value) => `Interface: ${value}`,
  },
];

const TOKEN_TRANSLATIONS: Array<[string, string]> = [
  ['应用市场', 'Marketplace'],
  ['管理后台', 'Admin Console'],
  ['系统配置', 'System Configuration'],
  ['用户管理', 'User Management'],
  ['应用管理', 'Application Management'],
  ['AI助手', 'AI Assistant'],
  ['统计', 'Statistics'],
  ['设置', 'Settings'],
  ['主题', 'Theme'],
  ['语言', 'Language'],
  ['界面', 'UI'],
  ['编程', 'Programming'],
  ['工作区', 'Workspace'],
  ['文件', 'File'],
  ['分享', 'Share'],
  ['帮助', 'Help'],
  ['示例', 'Examples'],
  ['登录', 'Sign In'],
  ['退出登录', 'Sign Out'],
  ['用户名', 'Username'],
  ['密码', 'Password'],
  ['保存', 'Save'],
  ['另存为', 'Save As'],
  ['导入', 'Import'],
  ['导出', 'Export'],
  ['上传', 'Upload'],
  ['下载', 'Download'],
  ['设备', 'Device'],
  ['日志', 'Log'],
  ['调试', 'Debug'],
  ['变量', 'Variable'],
  ['监视', 'Watch'],
  ['角色', 'Role'],
  ['道具', 'Prop'],
  ['背景', 'Backdrop'],
  ['对象', 'Object'],
  ['位置', 'Position'],
  ['连接', 'Connect'],
  ['断开', 'Disconnect'],
  ['蓝牙', 'Bluetooth'],
  ['串口', 'Serial'],
  ['语音', 'Speech'],
  ['识别', 'Recognition'],
  ['播报', 'Speech Output'],
  ['模型', 'Model'],
  ['音色', 'Voice'],
  ['打开', 'Open'],
  ['新建', 'New'],
  ['创建', 'Create'],
  ['编辑', 'Edit'],
  ['删除', 'Delete'],
  ['取消', 'Cancel'],
  ['确认', 'Confirm'],
  ['运行', 'Run'],
  ['停止', 'Stop'],
  ['烧录', 'Flash'],
  ['部署', 'Deploy'],
  ['收藏', 'Favorite'],
  ['评分', 'Rating'],
  ['我的', 'My'],
  ['作品', 'Project'],
  ['标题', 'Title'],
  ['描述', 'Description'],
  ['本地', 'Local'],
  ['服务器', 'Server'],
  ['暂无', 'No'],
  ['可用', 'Available'],
  ['已', ''],
  ['失败', 'Failed'],
  ['成功', 'Success'],
  ['请先', 'Please first'],
  ['然后', 'then'],
  ['选择', 'Select'],
  ['输入', 'Enter'],
  ['搜索', 'Search'],
  ['加载中', 'Loading'],
  ['保留所有权利', 'All rights reserved'],
];

const textOriginalMap = new WeakMap<Text, string>();
const attrOriginalMap = new WeakMap<Element, Map<string, string>>();
const SKIP_TRANSLATION_SELECTOR = [
  '[data-no-auto-i18n="true"]',
  'code',
  'pre',
  '.cm-editor',
  '.monaco-editor',
  '.ace_editor',
  '[contenteditable="true"]',
].join(',');

const normalizeUiLanguageSetting = (value: unknown): UiLanguageSetting => {
  if (value === 'zh-CN' || value === 'en-US' || value === 'auto') {
    return value;
  }
  return DEFAULT_UI_LANGUAGE;
};

const detectSystemUiLanguage = (): ResolvedUiLanguage => {
  if (typeof navigator === 'undefined') return 'zh-CN';
  const lang = String(navigator.language || 'zh-CN').toLowerCase();
  if (lang.startsWith('en')) return 'en-US';
  return 'zh-CN';
};

const resolveUiLanguage = (setting: UiLanguageSetting): ResolvedUiLanguage => {
  if (setting === 'auto') return detectSystemUiLanguage();
  return setting;
};

const translateToEnglish = (input: string): string => {
  const raw = input.trim();
  if (!raw) return input;

  if (EXACT_TRANSLATIONS[raw]) {
    return input.replace(raw, EXACT_TRANSLATIONS[raw]);
  }

  for (const rule of PATTERN_TRANSLATIONS) {
    const matched = raw.match(rule.pattern);
    if (matched) {
      const next = rule.replace(...matched);
      return input.replace(raw, next);
    }
  }

  if (!/[\u4e00-\u9fa5]/.test(raw)) return input;

  let transformed = raw;
  TOKEN_TRANSLATIONS
    .sort((a, b) => b[0].length - a[0].length)
    .forEach(([zh, en]) => {
      transformed = transformed.split(zh).join(en);
    });

  transformed = transformed
    .replace(/（/g, ' (')
    .replace(/）/g, ')')
    .replace(/：/g, ': ')
    .replace(/，/g, ', ')
    .replace(/。/g, '.')
    .replace(/、/g, ', ')
    .replace(/\s+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();

  if (!transformed) {
    return input;
  }

  return input.replace(raw, transformed);
};

const translateByLanguage = (text: string, language: ResolvedUiLanguage): string => {
  if (language === 'zh-CN') return text;
  return translateToEnglish(text);
};

export const translateUiText = translateByLanguage;

const shouldSkipElement = (element: Element | null): boolean => {
  if (!element) return true;
  const tagName = element.tagName.toLowerCase();
  if (tagName === 'script' || tagName === 'style') return true;
  return Boolean(element.closest(SKIP_TRANSLATION_SELECTOR));
};

const shouldSkipTextNode = (node: Text): boolean => {
  const parent = node.parentElement;
  return shouldSkipElement(parent);
};

const applyTextNode = (node: Text, language: ResolvedUiLanguage, captureFreshOriginal: boolean): void => {
  if (shouldSkipTextNode(node)) return;

  const currentText = node.nodeValue || '';
  const visibleText = currentText.trim();
  if (!visibleText) return;

  if (captureFreshOriginal || !textOriginalMap.has(node)) {
    textOriginalMap.set(node, currentText);
  }

  const originalText = textOriginalMap.get(node) || currentText;
  const translatedText = translateByLanguage(originalText, language);
  if (translatedText !== currentText) {
    node.nodeValue = translatedText;
  }
};

const ATTRIBUTE_NAMES = ['placeholder', 'title', 'aria-label', 'data-title'] as const;

const getElementOriginalAttributeMap = (element: Element): Map<string, string> => {
  const existing = attrOriginalMap.get(element);
  if (existing) return existing;
  const created = new Map<string, string>();
  attrOriginalMap.set(element, created);
  return created;
};

const applyElementAttributes = (element: Element, language: ResolvedUiLanguage, captureFreshOriginal: boolean): void => {
  if (shouldSkipElement(element)) return;

  const originalMap = getElementOriginalAttributeMap(element);

  ATTRIBUTE_NAMES.forEach((name) => {
    const current = element.getAttribute(name);
    if (current == null) return;

    if (captureFreshOriginal || !originalMap.has(name)) {
      originalMap.set(name, current);
    }

    const original = originalMap.get(name) || current;
    const translated = translateByLanguage(original, language);
    if (translated !== current) {
      element.setAttribute(name, translated);
    }
  });

  if (element instanceof HTMLInputElement) {
    if (element.type === 'button' || element.type === 'submit') {
      const current = element.value;
      if (current) {
        if (captureFreshOriginal || !originalMap.has('value')) {
          originalMap.set('value', current);
        }
        const original = originalMap.get('value') || current;
        const translated = translateByLanguage(original, language);
        if (translated !== current) {
          element.value = translated;
        }
      }
    }
  }
};

const walkAndApply = (root: Node, language: ResolvedUiLanguage, captureFreshOriginal: boolean): void => {
  if (root.nodeType === Node.TEXT_NODE) {
    applyTextNode(root as Text, language, captureFreshOriginal);
    return;
  }

  if (!(root instanceof Element || root instanceof Document || root instanceof DocumentFragment)) {
    return;
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let current: Node | null = walker.currentNode;
  while (current) {
    if (current.nodeType === Node.TEXT_NODE) {
      applyTextNode(current as Text, language, captureFreshOriginal);
    } else if (current.nodeType === Node.ELEMENT_NODE) {
      applyElementAttributes(current as Element, language, captureFreshOriginal);
    }
    current = walker.nextNode();
  }
};

let activeObserver: MutationObserver | null = null;
let applying = false;

const applyBlocklyLanguage = (language: ResolvedUiLanguage): void => {
  try {
    const locale = language === 'en-US' ? (BlocklyEn as Record<string, string>) : (BlocklyZhHans as Record<string, string>);
    Blockly.setLocale(locale);
  } catch {
    // Ignore locale update errors and keep current Blockly locale.
  }
};

const startRuntimeTranslator = (language: ResolvedUiLanguage): (() => void) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  activeObserver?.disconnect();
  activeObserver = null;

  const applyAll = (captureFreshOriginal: boolean): void => {
    if (!document.body) return;
    applying = true;
    try {
      walkAndApply(document.body, language, captureFreshOriginal);
    } finally {
      applying = false;
    }
  };

  applyAll(false);

  const observer = new MutationObserver((mutations) => {
    if (applying) return;

    mutations.forEach((mutation) => {
      if (mutation.type === 'characterData') {
        const target = mutation.target;
        if (target instanceof Text) {
          applying = true;
          try {
            applyTextNode(target, language, true);
          } finally {
            applying = false;
          }
        }
        return;
      }

      if (mutation.type === 'attributes') {
        if (mutation.target instanceof Element) {
          applying = true;
          try {
            applyElementAttributes(mutation.target, language, true);
          } finally {
            applying = false;
          }
        }
        return;
      }

      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          applying = true;
          try {
            walkAndApply(node, language, true);
          } finally {
            applying = false;
          }
        });
      }
    });
  });

  if (document.body) {
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: [...ATTRIBUTE_NAMES, 'value'],
    });
  }

  activeObserver = observer;

  return () => {
    observer.disconnect();
    if (activeObserver === observer) {
      activeObserver = null;
    }
  };
};

interface UiLanguageContextValue {
  setting: UiLanguageSetting;
  resolvedLanguage: ResolvedUiLanguage;
  antdLocale: typeof zhCN;
  setSetting: (next: UiLanguageSetting) => void;
  toggleBetweenChineseAndEnglish: () => void;
  t: (text: string) => string;
}

const UiLanguageContext = createContext<UiLanguageContextValue | null>(null);

export const UiLanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [setting, setSettingState] = useState<UiLanguageSetting>(() => {
    const settings = storage.getSettings();
    return normalizeUiLanguageSetting(settings.uiLanguage);
  });
  const [systemLanguageVersion, setSystemLanguageVersion] = useState(0);

  const resolvedLanguage = useMemo(() => resolveUiLanguage(setting), [setting, systemLanguageVersion]);

  const setSetting = useCallback((next: UiLanguageSetting) => {
    const normalized = normalizeUiLanguageSetting(next);
    storage.saveSettings({ uiLanguage: normalized });
    setSettingState(normalized);
  }, []);

  const toggleBetweenChineseAndEnglish = useCallback(() => {
    const currentResolved = resolveUiLanguage(setting);
    const next: UiLanguageSetting = currentResolved === 'zh-CN' ? 'en-US' : 'zh-CN';
    setSetting(next);
  }, [setSetting, setting]);

  const t = useCallback((text: string) => translateByLanguage(text, resolvedLanguage), [resolvedLanguage]);

  const antdLocale = useMemo(() => (resolvedLanguage === 'en-US' ? enUS : zhCN), [resolvedLanguage]);

  const stopTranslatorRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const handleLanguageChange = () => {
      if (setting === 'auto') {
        setSystemLanguageVersion((value) => value + 1);
      }
    };

    window.addEventListener('languagechange', handleLanguageChange);
    return () => {
      window.removeEventListener('languagechange', handleLanguageChange);
    };
  }, [setting]);

  useEffect(() => {
    document.documentElement.setAttribute('lang', resolvedLanguage === 'en-US' ? 'en-US' : 'zh-CN');
    document.documentElement.setAttribute('data-ui-language', resolvedLanguage);

    applyBlocklyLanguage(resolvedLanguage);

    stopTranslatorRef.current?.();
    stopTranslatorRef.current = startRuntimeTranslator(resolvedLanguage);

    window.dispatchEvent(new CustomEvent(UI_LANGUAGE_EVENT, {
      detail: {
        setting,
        resolvedLanguage,
      },
    }));
  }, [resolvedLanguage, setting]);

  useEffect(() => () => {
    stopTranslatorRef.current?.();
  }, []);

  const value = useMemo<UiLanguageContextValue>(() => ({
    setting,
    resolvedLanguage,
    antdLocale,
    setSetting,
    toggleBetweenChineseAndEnglish,
    t,
  }), [antdLocale, resolvedLanguage, setSetting, setting, t, toggleBetweenChineseAndEnglish]);

  return (
    <UiLanguageContext.Provider value={value}>
      {children}
    </UiLanguageContext.Provider>
  );
};

export const useUiLanguage = (): UiLanguageContextValue => {
  const context = useContext(UiLanguageContext);
  if (!context) {
    throw new Error('useUiLanguage must be used within UiLanguageProvider');
  }
  return context;
};

export const uiLanguageEventName = UI_LANGUAGE_EVENT;

export const getResolvedUiLanguageFromStorage = (): ResolvedUiLanguage => {
  const settings = storage.getSettings();
  const setting = normalizeUiLanguageSetting(settings.uiLanguage);
  return resolveUiLanguage(setting);
};
